import React, { useRef, useEffect, useState } from 'react';
import { Award, Target, Zap, TrendingUp } from 'lucide-react';
import { aboutContent } from '../data/mock';

const AboutSection = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.dataset.elementId;
            setVisibleElements((prev) => new Set([...prev, elementId]));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px' }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-50 via-white to-zinc-50 opacity-50"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block mb-3">
            <span className="text-yellow-600 text-xs font-semibold tracking-widest uppercase">Our Story</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-black mb-5 leading-tight">
            About <span className="text-yellow-500">RK ROTOARC</span>
          </h2>
          <p className="text-zinc-600 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            {aboutContent.description}
          </p>
        </div>

        {/* Timeline Section */}
        <div className="mb-20">
          <h3 className="text-2xl font-semibold text-black mb-12 text-center">Our Journey</h3>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-yellow-500 via-yellow-400 to-yellow-300 hidden lg:block"></div>

            {/* Timeline Items */}
            <div className="space-y-16">
              {/* 2015 - Establishment */}
              <div 
                data-element-id="timeline-1"
                ref={(el) => {
                  if (el && observerRef.current) {
                    observerRef.current.observe(el);
                  }
                }}
                className="flex flex-col lg:flex-row items-center gap-8 opacity-100 translate-x-0"
              >
                <div className="flex-1 lg:text-right">
                  <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow duration-300">
                    <h4 className="text-3xl font-bold text-yellow-600 mb-2">2015</h4>
                    <h5 className="text-xl font-semibold text-black mb-2">The Beginning</h5>
                    <p className="text-zinc-600 text-sm leading-relaxed">
                      RK ROTOARC was established with a vision to deliver world-class welding equipment to Indian industries. Trademark registered and first product line launched.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500 border-4 border-white shadow-lg z-10"></div>
                <div className="flex-1 hidden lg:block"></div>
              </div>

              {/* Growth Phase */}
              <div 
                data-element-id="timeline-2"
                ref={(el) => {
                  if (el && observerRef.current) {
                    observerRef.current.observe(el);
                  }
                }}
                className="flex flex-col lg:flex-row items-center gap-8 opacity-100 translate-x-0"
              >
                <div className="flex-1 hidden lg:block"></div>
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500 border-4 border-white shadow-lg z-10"></div>
                <div className="flex-1 lg:text-left">
                  <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow duration-300">
                    <h4 className="text-3xl font-bold text-yellow-600 mb-2">2018-2020</h4>
                    <h5 className="text-xl font-semibold text-black mb-2">Rapid Expansion</h5>
                    <p className="text-zinc-600 text-sm leading-relaxed">
                      Expanded product portfolio to include ISI certified cutting equipment, PPE, and drying ovens. Built strong dealer network across India.
                    </p>
                  </div>
                </div>
              </div>

              {/* Present Day */}
              <div 
                data-element-id="timeline-3"
                ref={(el) => {
                  if (el && observerRef.current) {
                    observerRef.current.observe(el);
                  }
                }}
                className="flex flex-col lg:flex-row items-center gap-8 opacity-100 translate-x-0"
              >
                <div className="flex-1 lg:text-right">
                  <div className="bg-gradient-to-br from-yellow-50 to-white border-2 border-yellow-500 rounded-xl p-6 shadow-lg">
                    <h4 className="text-3xl font-bold text-yellow-600 mb-2">Today</h4>
                    <h5 className="text-xl font-semibold text-black mb-2">Industry Leader</h5>
                    <p className="text-zinc-600 text-sm leading-relaxed">
                      Trusted by 1000+ clients across construction, manufacturing, and fabrication industries. Committed to innovation and quality excellence.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500 border-4 border-white shadow-lg z-10 animate-pulse"></div>
                <div className="flex-1 hidden lg:block"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div 
          data-element-id="stats"
          ref={(el) => {
            if (el && observerRef.current) {
              observerRef.current.observe(el);
            }
          }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20 opacity-100 translate-y-0"
        >
          <div className="text-center p-6 bg-white border border-zinc-200 rounded-xl">
            <div className="text-4xl font-bold text-yellow-500 mb-2">2015</div>
            <div className="text-zinc-600 text-sm font-medium">Established</div>
          </div>
          <div className="text-center p-6 bg-white border border-zinc-200 rounded-xl">
            <div className="text-4xl font-bold text-yellow-500 mb-2">50+</div>
            <div className="text-zinc-600 text-sm font-medium">Products</div>
          </div>
          <div className="text-center p-6 bg-white border border-zinc-200 rounded-xl">
            <div className="text-4xl font-bold text-yellow-500 mb-2">1000+</div>
            <div className="text-zinc-600 text-sm font-medium">Happy Clients</div>
          </div>
          <div className="text-center p-6 bg-white border border-zinc-200 rounded-xl">
            <div className="text-4xl font-bold text-yellow-500 mb-2">100%</div>
            <div className="text-zinc-600 text-sm font-medium">Made in India</div>
          </div>
        </div>

        {/* Value Props - Cleaner Design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div 
            data-element-id="value-1"
            ref={(el) => {
              if (el && observerRef.current) {
                observerRef.current.observe(el);
              }
            }}
            className="bg-white border border-zinc-200 rounded-xl p-6 hover:border-yellow-500 hover:shadow-xl transition-all duration-500"
          >
            <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center mb-4">
              <Award className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-2">Quality Commitment</h3>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Premium materials like forged brass and copper for industrial-grade durability.
            </p>
          </div>

          <div 
            data-element-id="value-2"
            ref={(el) => {
              if (el && observerRef.current) {
                observerRef.current.observe(el);
              }
            }}
            className="bg-white border border-zinc-200 rounded-xl p-6 hover:border-yellow-500 hover:shadow-xl transition-all duration-500"
          >
            <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-2">Industry Expertise</h3>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Years of experience in equipment trading and distribution excellence.
            </p>
          </div>

          <div 
            data-element-id="value-3"
            ref={(el) => {
              if (el && observerRef.current) {
                observerRef.current.observe(el);
              }
            }}
            className="bg-white border border-zinc-200 rounded-xl p-6 hover:border-yellow-500 hover:shadow-xl transition-all duration-500"
          >
            <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-2">Heavy Duty Build</h3>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Designed for continuous operation in harsh industrial environments.
            </p>
          </div>

          <div 
            data-element-id="value-4"
            ref={(el) => {
              if (el && observerRef.current) {
                observerRef.current.observe(el);
              }
            }}
            className="bg-white border border-zinc-200 rounded-xl p-6 hover:border-yellow-500 hover:shadow-xl transition-all duration-500"
          >
            <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-2">Trusted Growth</h3>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Preferred choice of professionals across India since 2015.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;